import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import {
  useCreateEmailTemplateMutation,
  useGetEmailTemplateQuery,
  useListEmailTemplatesMergeTagsQuery,
  useUpdateEmailTemplateMutation,
} from 'api/client-domain/email-template-service/email-template-service';
import {
  AllMergeTagsAndSpecialLinks,
  EmailTemplateResult,
} from 'api/client-domain/email-template-service/email-template-service.types';
import { IncDrawerType } from 'components/drawers/inc-drawer/inc-drawer.component';
import { useDrawer } from 'components/drawers/inc-drawer/inc-drawer.hooks';
import { emailTemplateFormSchema } from 'components/forms/email-template-form/email-template-form.schema';
import { ExportHtmlResult } from 'embed/Config';
import { useQueryWithForm } from 'hooks/rtkq.hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { EditorRef, UnlayerOptions } from 'react-email-editor';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectType } from 'redux/app/theme/theme.selectors';
import {
  EmailEditorDrawerDataType,
  EmailEditorDrawerProps,
} from './email-editor-drawer.component';
import { replaceBadImages, validate } from './email-editor-drawer.utils';

const makeTags = (
  mergeTagsData: AllMergeTagsAndSpecialLinks,
  template: Partial<EmailTemplateResult>,
  type: keyof AllMergeTagsAndSpecialLinks,
) => {
  if (!mergeTagsData || !template) return {};
  if (template?.emailTemplateType === 'EVENT') {
    return mergeTagsData[type][template?.emailTemplateType][
      template?.eventType
    ];
  } else {
    return mergeTagsData[type][template?.emailTemplateType];
  }
};

const useEmailEditorDrawerHandler = ({
  isOpen,
  close,
  data: drawerData,
  ...rest
}: IncDrawerType<EmailEditorDrawerDataType>) => {
  const emailEditorRef = useRef<EditorRef>(null);
  const [drawerWidth, setDrawerWidth] = useState<any[]>([
    '100%',
    '100%',
    '100%',
    window.innerWidth - 240,
  ]);
  const [isEmailEditorLoad, setEmailEditorLoad] = useState<boolean>(true);
  const [error, setError] = useState<ReturnType<typeof validate> | null>(null);
  const { data: mergeTagsData } = useListEmailTemplatesMergeTagsQuery(null);

  const form = useForm({
    resolver: yupResolver(emailTemplateFormSchema),
  });

  const themeType = useSelector(selectType);

  const exportHtml = (): Promise<ExportHtmlResult> => {
    const unlayer = emailEditorRef.current?.editor;

    return new Promise(resolve => {
      unlayer?.exportHtml(html => {
        resolve(html);
      });
    });
  };

  const mergeTags = useMemo(
    () =>
      makeTags(
        mergeTagsData,
        drawerData?.template,
        'mergeTags',
      ) as UnlayerOptions['mergeTags'],
    [drawerData?.template, mergeTagsData],
  );
  const specialLinks = useMemo(
    () =>
      makeTags(
        mergeTagsData,
        drawerData?.template,
        'specialLinks',
      ) as UnlayerOptions['specialLinks'],
    [drawerData?.template, mergeTagsData],
  );
  const requiredMergeTags = useMemo(
    () =>
      makeTags(
        mergeTagsData,
        drawerData?.template,
        'requiredMergeTags',
      ) as UnlayerOptions['mergeTags'],
    [drawerData?.template, mergeTagsData],
  );
  const requiredSpecialLinks = useMemo(
    () =>
      makeTags(
        mergeTagsData,
        drawerData?.template,
        'requiredSpecialLinks',
      ) as UnlayerOptions['specialLinks'],
    [drawerData?.template, mergeTagsData],
  );

  const handleSubmit = async (
    submitFn: (data: any, exportData: any) => Promise<any>,
  ) => {
    try {
      form.handleSubmit(async formData => {
        const rawData = await exportHtml();
        const data = {
          ...rawData,
          html: replaceBadImages(rawData.html),
          designString: replaceBadImages(JSON.stringify(rawData.design)),
        };

        const { mergeTagErrors, specialLinkErrors, imageErrors } = validate(
          data.designString,
          data.html,
          requiredMergeTags,
          requiredSpecialLinks,
        );

        if (
          mergeTagErrors.length > 0 ||
          specialLinkErrors.length > 0 ||
          imageErrors
        ) {
          setError({ mergeTagErrors, specialLinkErrors, imageErrors });
          return;
        } else {
          setError(null);
        }

        await submitFn(formData, data);
        await setEmailEditorLoad(true);
        onCloseEditor();
      })();
    } catch (e) {}
  };

  const onReadyEditor = () => {
    setEmailEditorLoad(false);
  };

  const onCloseEditor: typeof close = () => {
    setError(null);
    form.reset({});
    setEmailEditorLoad(true);
    close();
  };

  useEffect(() => {
    const handleResize = () => {
      setDrawerWidth(['100%', '100%', '100%', window.innerWidth - 240]);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return {
    drawerProps: {
      ...rest,
      isOpen,
      width: drawerWidth,
      data: drawerData,
      close: onCloseEditor,
    },
    editorProps: {
      ref: emailEditorRef,
      onReady: onReadyEditor,
    },
    handleSubmit,
    themeType,
    form,
    error,
    setError,
    mergeTags,
    requiredMergeTags,
    specialLinks,
    requiredSpecialLinks,
    exportHtml,
    isEmailEditorLoad,
    setEmailEditorLoad,
  };
};

export const useCreateEmailEditorDrawer = (
  drawerData?: EmailEditorDrawerDataType,
): EmailEditorDrawerProps => {
  const drawerProps = useDrawer<EmailEditorDrawerDataType>(drawerData);
  const {
    form,
    exportHtml,
    requiredMergeTags,
    requiredSpecialLinks,
    setError,
    setEmailEditorLoad,
    handleSubmit,
    isEmailEditorLoad,
    editorProps,
    ...rest
  } = useEmailEditorDrawerHandler(drawerProps);

  const [createEmailTemplate, { isLoading: isCreateLoading }] =
    useCreateEmailTemplateMutation();

  useEffect(() => {
    if (drawerProps.data?.template)
      form.reset(emailTemplateFormSchema.cast(drawerProps.data.template));
  }, [drawerProps.data, form]);

  useEffect(() => {
    if (drawerProps.data?.template.jsonTemplate && !isEmailEditorLoad)
      editorProps.ref.current?.editor.loadDesign(
        JSON.parse(drawerProps.data?.template.jsonTemplate),
      );
  }, [
    drawerProps.data?.template.jsonTemplate,
    editorProps.ref,
    isEmailEditorLoad,
  ]);

  const submit = async () => {
    try {
      handleSubmit(async (formData, exportData) => {
        const requestBody = {
          name: formData.name,
          subject: formData.subject,
          jsonTemplate: exportData.designString,
          htmlTemplate: exportData.html,
          emailTemplateType: drawerProps.data?.template?.emailTemplateType,
          reusable: drawerProps.data?.template?.reusable,
        };

        const response = await createEmailTemplate(requestBody).unwrap();

        drawerProps.data?.setFormFieldValue?.({
          id: response.templateId,
          label: response.name,
        });

        return response;
      });
    } catch (e) {}
  };

  return {
    ...rest,
    submitLoading: isCreateLoading,
    isLoading: isEmailEditorLoad || isCreateLoading,
    form,
    submit,
    editorProps,
  };
};

export const useEditEmailEditorDrawer = (
  drawerData?: EmailEditorDrawerDataType,
): EmailEditorDrawerProps => {
  const drawerProps = useDrawer<EmailEditorDrawerDataType>(drawerData);
  const {
    form,
    exportHtml,
    requiredMergeTags,
    requiredSpecialLinks,
    setError,
    setEmailEditorLoad,
    handleSubmit,
    isEmailEditorLoad,
    editorProps,
    ...rest
  } = useEmailEditorDrawerHandler(drawerProps);

  const [updateEmailTemplate, { isLoading: isUpdateLoading }] =
    useUpdateEmailTemplateMutation();
  const getEmailTemplateQueryResult = useGetEmailTemplateQuery(
    drawerProps.data?.template?.templateId
      ? { id: drawerProps.data?.template?.templateId }
      : skipToken,
  );
  const { currentData: templateData, isLoading: isTemplateLoading } =
    getEmailTemplateQueryResult;

  useQueryWithForm(form, getEmailTemplateQueryResult, emailTemplateFormSchema);

  useEffect(() => {
    if (drawerProps.isOpen && getEmailTemplateQueryResult.isSuccess) {
      getEmailTemplateQueryResult.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerProps.isOpen, getEmailTemplateQueryResult.isSuccess]);

  useEffect(() => {
    if (templateData && !isEmailEditorLoad)
      editorProps.ref.current?.editor.loadDesign(
        JSON.parse(templateData.jsonTemplate),
      );
  }, [editorProps.ref, isEmailEditorLoad, templateData]);

  const submit = async () => {
    try {
      handleSubmit(async (formData, exportData) => {
        const body = {
          templateId: drawerProps.data?.template?.templateId,
          name: formData.name,
          subject: formData.subject,
          jsonTemplate: exportData.designString,
          htmlTemplate: exportData.html,
          emailTemplateType: drawerProps.data?.template?.emailTemplateType,
          reusable: drawerProps.data?.template?.reusable,
          eventType: drawerProps.data?.template?.eventType,
          fromAddress: drawerProps.data?.template?.fromAddress,
          bcc: drawerProps.data?.template?.bcc,
        };

        const response = await updateEmailTemplate({
          id: drawerProps.data?.template?.templateId,
          body,
        }).unwrap();

        return response;
      });
    } catch (e) {}
  };

  return {
    ...rest,
    submitLoading: isUpdateLoading,
    isLoading: isEmailEditorLoad || isUpdateLoading || isTemplateLoading,
    editorProps,
    form,
    submit,
  };
};

export default useEmailEditorDrawerHandler;
